import type { MetricBenchmark } from "./booked-call-metric-benchmarks";

// $200K/month plan for the Book a Call funnel (Joey Forslund, Oct 19, 2026): Jeremy Haynes AI's final benchmark
// answer (Scenario A: hold a $6,000 average sale price), built on the Aug 20–Oct 19 pace, cross-checked against
// his "200k-month-targets.xlsx". Jeremy's rules, as used on the Sheets view:
//   a full Mon–Sun week column  -> his WEEKLY target
//   any other column of N days  -> his DAILY target × N (a day = daily; a clipped 3-day week = daily × 3)
//   both scale with the target bar: × (monthly revenue target ÷ $200,000)
// Rate targets (show rate, close rate, ASP, …) are the same for every column and never scale.
// Where Jeremy gave no daily number, daily = weekly ÷ 7.
export const PLAN_BASE_MONTHLY_REVENUE = 200_000;
export const DEFAULT_MONTHLY_REVENUE_TARGET = 200_000;

export type PlanVolume = { weekly: number; daily: number; money?: boolean; source: string };
export const PLAN_WEEKLY_VOLUME: Record<string, PlanVolume> = {
  "Page Views": { weekly: 1050, daily: 150, source: "Jeremy: 150/day, 1,050/wk (xlsx C3)" },
  "Applications": { weekly: 136, daily: 19, source: "Jeremy: 19/day, 136/wk (xlsx C5)" },
  "Leads": { weekly: 136, daily: 19, source: "Jeremy: 19/day, 136/wk (xlsx C5)" },
  "Overall Qualified Leads": { weekly: 57, daily: 8, source: "Jeremy: 8/day, 57/wk (xlsx C7)" },
  "Booked Calls": { weekly: 25, daily: 3, source: "Jeremy: 25 inbound/wk; 5/day incl. 2 outbound = 3 inbound/day (xlsx C10)" },
  "Total Booked Closing Calls (IB)": { weekly: 25, daily: 3, source: "Jeremy: 25 inbound/wk; 5/day incl. 2 outbound = 3 inbound/day (xlsx C10)" },
  "Book Calls Outbound": { weekly: 12, daily: 2, source: "Jeremy: 2+ outbound/day, 12/wk (xlsx C11)" },
  "Total Booked Closing Calls (OB)": { weekly: 12, daily: 2, source: "Jeremy: 2+ outbound/day, 12/wk (xlsx C11)" },
  "Total Booked Closing Calls (All)": { weekly: 37, daily: 5, source: "Jeremy: 5/day, 37/wk (xlsx C9)" },
  "Showed Calls": { weekly: 26, daily: 4, source: "Jeremy: 4/day, 26/wk (xlsx C14)" },
  "Total Closing Calls Completed": { weekly: 26, daily: 4, source: "Jeremy: 4 showed/day, 26/wk (xlsx C14)" },
  "Closed Deals": { weekly: 8, daily: 1, source: "Jeremy: 1/day, 8/wk (xlsx C16)" },
  "Deals Closed": { weekly: 8, daily: 1, source: "Jeremy: 1/day, 8/wk (xlsx C16)" },
  "Revenue Generated": { weekly: 46000, daily: 6650, money: true, source: "Jeremy: $6,650/day, $46,000/wk (xlsx C19)" },
  "Cash Collected": { weekly: 23000, daily: 3300, money: true, source: "Jeremy: $3,300/day, $23,000/wk (xlsx C20)" },
  "VSL Plays": { weekly: 300, daily: 43, source: "Jeremy: 43/day, 300+/wk (xlsx C23 said 490)" },
  "VSL Unique Plays": { weekly: 250, daily: 36, source: "Jeremy: 36/day, 250+/wk (xlsx C24 said 400)" },
  "EOCs": { weekly: 40, daily: 40 / 7, source: "Jeremy: 40+/wk (no daily given: weekly ÷ 7)" },
};

export type PlanRate = Pick<MetricBenchmark, "target" | "direction" | "min" | "max"> & { source: string };
export const PLAN_RATES: Record<string, PlanRate> = {
  "Application Rate": { target: "13%+", direction: "min", min: 13, source: "C4 Application Rate" },
  "Show Rate": { target: "70%+", direction: "min", min: 70, source: "C13 Show Rate" },
  "Closing Show %": { target: "70%+", direction: "min", min: 70, source: "C13 Show Rate" },
  "Close Rate": { target: "30%+", direction: "min", min: 30, source: "C15 Close Rate" },
  "Closing %": { target: "30%+", direction: "min", min: 30, source: "C15 Close Rate" },
  // Book a call's real ASP, Sep 1–Oct 20, 2026 (15 deals, $89,000), replacing the $6,000 offer price (Joey, Oct 20, 2026;
  // same number as VELOCITY_ASP_BY_FUNNEL.bookCall in config/funnel-targets.ts).
  "Average Sale Price": { target: "monthly avg", source: "No fixed target (Joey, Oct 20, 2026 7:33 PM ET): the actual monthly average cash per close (was $5,933, before that C17 $6,000)" },
  "Cash VS Rev %": { target: "50%+", direction: "min", min: 50, source: "C21 Cash vs Revenue %" },
  "Avg VSL Watch %": { target: "35%+", direction: "min", min: 35, source: "C25 Avg VSL Watch %" },
};

export type PlanOverride = Pick<MetricBenchmark, "target" | "direction" | "min" | "max">;

export function planScale(monthlyTarget: number) {
  return Number.isFinite(monthlyTarget) && monthlyTarget > 0 ? monthlyTarget / PLAN_BASE_MONTHLY_REVENUE : 1;
}

/** Days covered by an inclusive YYYY-MM-DD range. */
export function windowDays(startDate: string, endDate: string) {
  const start = Date.parse(`${startDate}T12:00:00.000Z`); const end = Date.parse(`${endDate}T12:00:00.000Z`);
  return Number.isFinite(start) && Number.isFinite(end) && end >= start ? Math.round((end - start) / 86400000) + 1 : 7;
}

/** Weekly target at the $200K baseline; a saved per-metric override (its `min`) replaces Jeremy's value. */
export function baseWeekly(metric: string, overrides: Record<string, PlanOverride> = {}) {
  const plan = PLAN_WEEKLY_VOLUME[metric];
  if (!plan) return null;
  const override = overrides[metric];
  return override?.direction === "min" && Number.isFinite(override.min) ? Number(override.min) : plan.weekly;
}

/** Volume target for a column of `days` days at the given monthly revenue target. */
export function volumeTarget(metric: string, days: number, monthlyTarget: number, overrides: Record<string, PlanOverride> = {}) {
  const plan = PLAN_WEEKLY_VOLUME[metric];
  const weekly = baseWeekly(metric, overrides);
  if (!plan || weekly === null) return null;
  // An edited weekly target replaces both: its day value is weekly ÷ 7.
  const daily = overrides[metric] ? weekly / 7 : plan.daily;
  const base = days === 7 ? weekly : daily * days;
  return base * planScale(monthlyTarget);
}

const whole = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const oneDecimal = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });
export function formatPlanValue(value: number, money = false) {
  const rounded = Math.abs(value) >= 100 ? whole.format(Math.round(value)) : oneDecimal.format(Math.round(value * 10) / 10);
  return money ? `$${rounded}` : rounded;
}

/** The benchmark a single Sheets column is scored against. */
export function columnBenchmark(metric: string, base: MetricBenchmark | undefined, days: number, monthlyTarget: number, overrides: Record<string, PlanOverride> = {}): MetricBenchmark | undefined {
  if (!base) return undefined;
  const plan = PLAN_WEEKLY_VOLUME[metric];
  if (plan) {
    const value = volumeTarget(metric, days, monthlyTarget, overrides)!;
    // A tiny tolerance keeps float noise (e.g. 19 × 3 × 0.75) from turning an exact hit red.
    return { ...base, target: `${formatPlanValue(value, plan.money)}+`, direction: "min", min: value - 1e-9, max: undefined };
  }
  const rate = PLAN_RATES[metric];
  const { source: _source, ...rateBenchmark } = rate ?? { source: "" };
  return { ...base, ...(rate ? rateBenchmark : {}), ...overrides[metric] };
}
